'use client';

import {
	createContext,
	useContext,
	useState,
	useEffect,
	ReactNode,
	useMemo,
	useCallback,
} from 'react';
import { authService } from '../services/authService';
import { User } from '@practium/shared';

interface AuthContextType {
	user: User | null;
	isAuthenticated: boolean;
	isLoading: boolean;
	logout: () => Promise<void>;
	refreshUser: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
	const [user, setUser] = useState<User | null>(null);
	const [isLoading, setIsLoading] = useState(true);

	const refreshUser = useCallback(() => {
		const currentUser = authService.getCurrentUser();
		setUser(currentUser);
	}, []);

	useEffect(() => {
		refreshUser();
		setIsLoading(false);
	}, [refreshUser]);

	useEffect(() => {
		const handleCustomStorageChange = () => {
			refreshUser();
		};

		if (typeof globalThis !== 'undefined') {
			globalThis.addEventListener('practium-auth-change', handleCustomStorageChange);

			return () => {
				globalThis.removeEventListener('practium-auth-change', handleCustomStorageChange);
			};
		}
	}, [refreshUser]);

	const logout = useCallback(async () => {
		await authService.logout();
		setUser(null);
	}, []);

	const value = useMemo(
		() => ({
			user,
			isAuthenticated: !!user,
			isLoading,
			logout,
			refreshUser,
		}),
		[user, isLoading, logout, refreshUser]
	);

	return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
	const context = useContext(AuthContext);
	if (!context) {
		throw new Error('useAuth must be used within AuthProvider');
	}
	return context;
};
